/* eslint-disable consistent-return */
/**
 * 소셜 플러그인 개발가이드 문서 : http://wiki.navercorp.com/pages/viewpage.action?pageId=256032483
 * 소셜 플러그인 관련 참고 이슈 : https://oss.navercorp.com/bizServiceLab/sogum-next/issues/9193
 */

import React, { useRef, useCallback } from 'react'

import classNames from 'classnames'
// import { N_CLICKS, URL } from 'src/constants'
import { useEffectOnce } from 'react-use'

import { darkModeVar } from '@web/hooks/useGlobalDetermineDarkMode'
import links from '@web/libs/constants/links'
import { lazyLoadResource } from '@web/libs/lazyLoadResource'
import { extractErrorMessage, getVersioningTimeId, neloLogger } from '@web/libs/utils'

import Clickable from '../Clickable'
import styles from './index.module.scss'

const SPLUGIN_DEFAULT_CLASS = 'naver-splugin spi_sns_share'

/**
 * @description 타임 버저닝 20분 단위 변경 관련 이슈 [#13893](https://oss.navercorp.com/place/local/issues/13893)
 */
const SOCIAL_PLUGIN_DURATION_MINUTES = 20

const getSocialPluginSrc = () => `${links.SOCIAL_PLUGIN}?v=${getVersioningTimeId(SOCIAL_PLUGIN_DURATION_MINUTES)}`

const loadSocialPlugin = (): Promise<Function> =>
  new Promise((resolve, reject) => {
    if (window.SocialPlugIn_Core) return resolve(window.SocialPlugIn_Core)
    lazyLoadResource(getSocialPluginSrc(), { type: 'js' })
    const timeoutSeconds = 1.5
    const intervalSeconds = 0.1

    let intervalCount = 0
    const checkSocialPluginCoreSetting = setInterval(() => {
      if (window.SocialPlugIn_Core) {
        clearInterval(checkSocialPluginCoreSetting)
        resolve(window.SocialPlugIn_Core)
      } else {
        if (++intervalCount * intervalSeconds > timeoutSeconds) reject(new Error('[loadSocialPlugin] Load Timeout'))
      }
    }, intervalSeconds * 1000)
  })

const initSocialPlugin = async ({ element, onInitComplete }: { element: HTMLElement; onInitComplete?: () => void }) => {
  if (!window.__splugin) {
    window.__splugin = window.SocialPlugIn_Core({
      evKey: 'local_application',
      serviceName: 'MY플레이스',
      dimmed: 'fixed',
      darkMode: darkModeVar(),
      onClick: null,
      onShow: null,
      onHide: null,
      onResize: null,
      onRotate: null,
    })
  }

  if (element) {
    window.__splugin.init(element)
    onInitComplete?.()
  }
}

interface Props {
  children?: React.ReactNode
  id?: string
  className?: string
  clickCode?: string
  title?: string
  url?: string
  imageUrl?: string
  description?: string
  kakao?: {
    buttonText?: string
  }
  isValidClick?: () => boolean
  buttonId?: string
}

const SocialPlugin = ({
  id,
  className,
  clickCode,
  url = 'window.location.href',
  title,
  description,
  kakao,
  imageUrl,
  isValidClick = () => true,
  buttonId,
  children,
}: Props) => {
  const buttonRef = useRef<HTMLDivElement>(null)

  useEffectOnce(() => {
    loadSocialPlugin()
      .then(() => initSocialPlugin({ element: buttonRef.current as HTMLDivElement }))
      .catch(error => neloLogger('warn', extractErrorMessage(error)))
  })

  // https://oss.navercorp.com/place/local/pull/3927
  useEffectOnce(() => {
    if (document?.documentElement?.style?.webkitUserSelect === 'none') {
      document.documentElement.style.webkitUserSelect = ''
      // @ts-ignore
      document.body.style.webkitTouchCallout = ''
      return function cleanup() {
        document.documentElement.style.webkitUserSelect = 'none'
        // @ts-ignore
        document.body.style.webkitTouchCallout = 'none'
      }
    }
  })

  const handleClick = useCallback(() => {
    if (!isValidClick?.()) return
    buttonRef.current?.click()
  }, [isValidClick])

  // <button /> should be located inside of <div/> tag: to avoid duplicated nClicks request
  return (
    <div
      ref={buttonRef}
      id={buttonId}
      className={classNames(className, SPLUGIN_DEFAULT_CLASS)}
      role="button"
      data-style="one-button"
      data-title={title}
      data-line-template-type="custom_web"
      data-line-title={title}
      data-line-image-url={imageUrl}
      data-kakaotalk-template-type="custom_web"
      data-kakaotalk-title={title}
      data-kakaotalk-image-url={imageUrl}
      data-kakaotalk-description={description}
      data-kakaotalk-button-text={kakao?.buttonText}
      data-twitter-description={description}
      data-line-description={description}
      data-url-extra={title}
      data-friend-display="on"
      data-kakaostory-display="off"
      data-url={url}
    >
      <Clickable id={id} nClickCode={clickCode} className={styles.button} type="button" onClick={handleClick}>
        {children ?? '공유'}
      </Clickable>
    </div>
  )
}

export default SocialPlugin
